import { PackageCheck } from 'lucide-react';
import type { FilterState } from '../dashboardFilterUtils';
import { FilterSection } from '../../filters/FilterSection';

interface CallOffSectionProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
}

/**
 * CALL OFF group — the measurement basis for call-off suppliers.
 *
 * This is a basis toggle across the whole combined view, not a supplier
 * filter. Ticked, call-off suppliers are measured from the date stock was
 * booked into their warehouse; every other supplier stays on GRN, because
 * the customer never receives their stock levels and so has no earlier
 * verifiable date. Both are blended into one OTIF score and one arrears
 * figure, so the total can be read from either angle in one place.
 * Unticked reverts everyone, call-off included, to a uniform GRN basis.
 *
 * The caller mounts this only when call-off is enabled for the company
 * (`switch_call_off_tab`), so a customer with no call-off suppliers never
 * sees a control that could not change their numbers.
 */
export function CallOffSection({ filters, setFilters }: CallOffSectionProps) {
  return (
    <FilterSection title="Call Off" icon={PackageCheck}>
      <label className="flex items-center gap-2 cursor-pointer w-fit">
        <input
          type="checkbox"
          checked={filters.callOffWarehouseBasis}
          onChange={(e) => setFilters({ ...filters, callOffWarehouseBasis: e.target.checked })}
          className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-2"
        />
        <span className="text-sm font-medium text-foreground">
          Measure call-off suppliers at warehouse booking
        </span>
      </label>
      <p className="text-xs text-muted-foreground mt-2">
        OTIF and historical arrears for call-off suppliers are measured from the date stock is
        booked into their warehouse. All other suppliers stay on the GRN date.
      </p>
    </FilterSection>
  );
}
